import { useState } from 'react';
import DOMPurify from 'dompurify';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchUserProfileView from '@CommonViews/SearchUserProfileView';
import SearchUserRow from '@CommonRepresentationalDisplay/SearchUserRow';
import TeamInfoView from '@CommonViews/TeamInfoView';
import ConfirmAddUserForm from '@CommonViews/ConfirmAddUserForm';
import { IoClose as Close, IoSearch as SearchIcon } from 'react-icons/io5';
import { MdPeople as People, MdGroups as Groups } from 'react-icons/md';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import TeamService from '@Helpers/URLHelpers/TeamService';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorHandler from '@Root/ErrorHandler';
import './AddUsersView.scss';

export default function AddUsersView(props) {

    const [loading, setLoading] = useState(false);
    const [userList, setUserList] = useState([]);
    const [teamList, setTeamList] = useState([]);
    const [showProfileView, setShowProfileView] = useState(false);
    const [showConfirmInviteForm, setShowConfirmInviteForm] = useState(false);
    const [profileId, setProfileId] = useState(0);
    const [selectedTeam, setSelectedTeam] = useState({});
    const [showSearchUser, setShowSearchUser] = useState(false);
    const [showTeamInfoView, setShowTeamInfoView] = useState(false);
    const [addUserId, setAddUserId] = useState();
    const [addTeamId, setAddTeamId] = useState();
    const [childType, setChildType] = useState();
    const [addUserFullName, setAddUserFullName] = useState();
    const [addTeamTitle, setAddTeamTitle] = useState();
    const [activeUsers, setActiveUsers] = useState({});
    const [activeTeams, setActiveTeams] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('users'); // users, teams
    const userInfoService = new UserInfoService();
    const teamService = new TeamService();
    const projectService = new ProjectService();
    const errorHandler = new ErrorHandler();
    const { activeUserId } = useReduxActiveUser();
    const { currentOrg, currentOrgId } = useReduxOrg();
    const className = 'addUsersView';

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        
        setLoading(true);
        setActiveUsers([]);
        setUserList([]);
        setTeamList([]);
        setShowSearchUser(false);
        
        try {
            let usersData;
            if(!!props.project && Object.keys(props.project).length !== 0 && props.project.parentType === 'org') {
                let filteredTeams = [];
                let teams;
                if(currentOrg !== null && currentOrg !== undefined && !!currentOrgId && currentOrgId > 0){
                    teams = await teamService.getTeamListByOrgId(currentOrgId);
                    teams = JSON.parse(teams.body);
                    for(var i = 0; i < teams.length; i++){
                        if(teams[i].teamName.toLowerCase().includes(searchQuery.toLowerCase())){
                            filteredTeams = [...filteredTeams, teams[i]]
                        }
                    }
                    setTeamList(filteredTeams);
                }
                usersData = await userInfoService.searchUserByKeywordInOrg(searchQuery, props.project.parentId);
                let projectUsers = await projectService.getUserPermissionListForProject(props.project.id);
                projectUsers = JSON.parse(projectUsers.body);
                setActiveUsers(projectUsers);
                let projectTeams = await projectService.getTeamPermissionListForProject(props.project.id);
                projectTeams = JSON.parse(projectTeams.body);
                setActiveTeams(projectTeams);
            } else if(Object.keys(props.team).length !== 0) {
                usersData = await userInfoService.searchUserByKeywordInOrg(searchQuery, props.team.orgId);
                let teamUsers = await teamService.getUserListByTeamId(props.team.id);
                teamUsers = JSON.parse(teamUsers.body);
                setActiveUsers(teamUsers);
            }
            usersData = JSON.parse(usersData.body);
            let users = [];
            for (let i = 0; i < usersData.length; i++) {
                if (usersData[i].id !== activeUserId) {
                    users.push(usersData[i]);
                }
            }
            setUserList(users);
            setShowSearchUser(true);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    }

    const addToActiveUsers = (userId) => {
        let userObj = {
            'id': userId
        }
        let activeUsersObj = activeUsers;
        activeUsersObj.push(userObj);
        setActiveUsers(activeUsersObj);
    }

    const handleSearchQueryChange = (e) => {
        let value = DOMPurify.sanitize(e.target.value);
        setSearchQuery(value);
    }

    const handleTeamDisable = (teamId) => {
        if((Object.keys(activeTeams).length !== 0) && (activeTeams?.some(item => item.id === teamId))) return true;
        return false;
    }

    const handleAddUser = (user, index) => {
        setAddUserId(user.id);
        setAddUserFullName(user.fullName);
        setChildType('user');
        toggleConfirmInviteFormView();
    }

    const handleAddTeam = (e, team, index) => {
        e?.preventDefault();
        setAddTeamId(team.id);
        setAddTeamTitle(team.teamName);
        setChildType('team');
        toggleConfirmInviteFormView();
    }

    const toggleProfileView = () => {
        setShowProfileView(!showProfileView)
    }

    const toggleTeamInfoView = () => {
        setShowTeamInfoView(!showTeamInfoView)
    }

    const toggleConfirmInviteFormView = () => {
        setShowConfirmInviteForm(!showConfirmInviteForm);
    }

    const handleUserClick = (userId) => {
        setProfileId(userId);
        setShowProfileView(!showProfileView);
    }

    const handleTeamClick = (team) => {
        setSelectedTeam(team);
        setShowTeamInfoView(!showTeamInfoView);
    }

    const onSearchKeyPress = (e) => {
        if(e.keyCode === 13) {
            handleSearch()
        }
    }

    const parentName = (!!props.project && Object.keys(props.project).length !== 0) 
        ? props.project?.projectName 
        : (!!props.team && Object.keys(props.team).length !== 0) 
        ? props.team?.teamName 
        : '';

    const filteredUsers = userList;
    const filteredTeams = teamList;
    const shouldShowUsers = activeTab === 'users';
    const shouldShowTeams = activeTab === 'teams';
    const showTeamTab = !!props.project && Object.keys(props.project).length !== 0 && props.parentType !== 'team';

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}>
            <div className={className}>
                <div className={`${className}__container`}>
                    {/* Header */}
                    <div className={`${className}__header`}>
                        <div className={`${className}__header__left`}>
                            <People className={`${className}__header__icon`} />
                            <div className={`${className}__header__info`}>
                                <h2 className={`${className}__header__title`}>Add Collaborators</h2>
                                <p className={`${className}__header__subtitle`}>
                                    Add users and teams to {parentName}
                                </p>
                            </div>
                        </div>
                        <button 
                            className={`${className}__header__close`} 
                            onClick={props.closePopup}
                            disabled={loading}>
                            <Close />
                        </button>
                    </div>

                    {/* Search Bar */}
                    <div className={`${className}__search`}>
                        <TextField
                            placeholder="Search by name, username, or email..."
                            value={searchQuery}
                            onChange={handleSearchQueryChange}
                            onKeyDown={onSearchKeyPress}
                            variant="outlined"
                            fullWidth
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon className={`${className}__search__icon`} />
                                    </InputAdornment>
                                ),
                                endAdornment: loading && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <button
                            className={`${className}__search__button`}
                            onClick={handleSearch}
                            disabled={loading || !searchQuery.trim()}>
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>

                    {/* Tabs (only show if teams can be added) */}
                    {showTeamTab && showSearchUser && (
                        <div className={`${className}__tabs`}>
                            <button
                                className={`${className}__tab ${activeTab === 'users' ? 'active' : ''}`}
                                onClick={() => setActiveTab('users')}>
                                <People />
                                Users ({filteredUsers.length})
                            </button>
                            <button
                                className={`${className}__tab ${activeTab === 'teams' ? 'active' : ''}`}
                                onClick={() => setActiveTab('teams')}>
                                <Groups />
                                Teams ({filteredTeams.length})
                            </button>
                        </div>
                    )}

                    {/* Results */}
                    <div className={`${className}__results`}>
                        {!showSearchUser ? (
                            <div className={`${className}__empty`}>
                                <div className={`${className}__empty__icon`}>
                                    <SearchIcon />
                                </div>
                                <div className={`${className}__empty__title`}>
                                    Search for collaborators
                                </div>
                                <div className={`${className}__empty__text`}>
                                    Enter a name, username, or email to find users and teams to add
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Users Results */}
                                {shouldShowUsers && (
                                    <div className={`${className}__section`}>
                                        {filteredUsers.length === 0 ? (
                                            <div className={`${className}__empty`}>
                                                <div className={`${className}__empty__icon`}>
                                                    <People />
                                                </div>
                                                <div className={`${className}__empty__title`}>
                                                    No users found
                                                </div>
                                                <div className={`${className}__empty__text`}>
                                                    Try adjusting your search query
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={`${className}__list`}>
                                                {filteredUsers.map((user, index) => (
                                                    <SearchUserRow
                                                        key={user.id}
                                                        user={user}
                                                        index={index}
                                                        activeUsers={activeUsers}
                                                        handleUserClick={() => handleUserClick(user.id)}
                                                        handleAddUser={() => handleAddUser(user, index)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Teams Results */}
                                {shouldShowTeams && (
                                    <div className={`${className}__section`}>
                                        {filteredTeams.length === 0 ? (
                                            <div className={`${className}__empty`}>
                                                <div className={`${className}__empty__icon`}>
                                                    <Groups />
                                                </div>
                                                <div className={`${className}__empty__title`}>
                                                    No teams found
                                                </div>
                                                <div className={`${className}__empty__text`}>
                                                    Try adjusting your search query
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={`${className}__list`}>
                                                {filteredTeams.map((team, index) => (
                                                    <div 
                                                        key={team.id} 
                                                        className={`${className}__teamItem`}>
                                                        <div className={`${className}__teamItem__left`}>
                                                            <div className={`${className}__teamItem__avatar`}>
                                                                <Groups />
                                                            </div>
                                                            <div className={`${className}__teamItem__info`}>
                                                                <div className={`${className}__teamItem__name`}>
                                                                    {team.teamName}
                                                                </div>
                                                                <div className={`${className}__teamItem__tag`}>
                                                                    {team.tag}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            className={`${className}__teamItem__button`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleAddTeam(e, team, index);
                                                            }}
                                                            disabled={handleTeamDisable(team.id)}>
                                                            {handleTeamDisable(team.id) ? 'Already Added' : 'Add Team'}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {showTeamInfoView && (
                    <TeamInfoView
                        open={showTeamInfoView}
                        closePopup={toggleTeamInfoView}
                        team={selectedTeam}
                    />
                )}
                
                {showProfileView && (
                    <SearchUserProfileView
                        open={showProfileView}
                        closePopup={toggleProfileView}
                        profileId={profileId}
                    />
                )}
                
                {showConfirmInviteForm && (
                    <ConfirmAddUserForm
                        open={showConfirmInviteForm}
                        closePopup={toggleConfirmInviteFormView}
                        userId={addUserId}
                        parentType={props.parentType}
                        addToActiveUsers={addToActiveUsers}
                        userFullName={addUserFullName}
                        teamId={addTeamId}
                        teamTitle={addTeamTitle}
                        project={props.project}
                        team={props.team}
                        childType={childType}
                        reloadData={props.reloadData}
                    />
                )}
            </div>
        </Modal>
    );
}
